<script setup lang="ts">
import { computed, ref } from 'vue'
import { vOnClickOutside } from '@vueuse/components'
import { useUserStore } from '@/modules/user/stores/user.store'
import { useAuthStore } from '@/modules/auth/stores/auth.store'
import {
  mdiCardAccountDetails,
  mdiAccountCog,
  mdiCommentQuestion,
  mdiLogout,
  mdiInformationOutline,
  mdiOpenInNew,
} from '@mdi/js'
import BaseButton from '@/shared/components/button/BaseButton.vue'
import SvgIcon from '@/shared/components/icon/SvgIcon.vue'
import ScaleTransition from '@/shared/components/transition/ScaleTransition.vue'
import UserProfileModal from './UserProfileModal.vue'
import UserSettingsModal from './UserSettingsModal.vue'
import InformationModal from './InformationModal.vue'

const authStore = useAuthStore()
const userStore = useUserStore()
const isActive = ref<boolean>(false)
const isProfileOpen = ref<boolean>(false)
const isAboutOpen = ref<boolean>(false)
const isSettingsOpen = ref<boolean>(false)

const initials = computed(
  (): string =>
    `${userStore.profile ? userStore.profile.firstName?.charAt(0) : ''}${userStore.profile ? userStore.profile.lastName?.charAt(0) : ''}`,
)

const signOut = async (): Promise<void> => {
  await authStore.logout()
  authStore.navToLogout()
}

const onProfile = (): void => {
  isActive.value = false
  isProfileOpen.value = true
}

const onSettings = (): void => {
  isActive.value = false
  isSettingsOpen.value = true
}

const onAbout = (): void => {
  isActive.value = false
  isAboutOpen.value = true
}
</script>

<template>
  <div v-if="userStore.profile" v-on-click-outside="() => (isActive = false)" class="relative">
    <BaseButton
      :title="$t('glossary.user')"
      @click="isActive = !isActive"
      class="flex items-center justify-center border-2 text-sm border-white h-8 w-8 overflow-hidden rounded-full hover:bg-gray-800"
      :class="isActive ? 'bg-gray-800' : 'bg-black'"
    >
      <span class="font-medium text-white uppercase">{{ initials }}</span>
    </BaseButton>
    <ScaleTransition>
      <div
        v-if="isActive"
        class="absolute right-0 z-40 mt-0.5 border border-gray-800 bg-black text-gray-300 origin-top-right shadow-md focus:outline-none"
      >
        <div v-if="userStore.profile" class="border-b border-gray-800 text-sm truncate p-2">
          {{ userStore.profile.organization }}
        </div>
        <div class="flex items-center p-4">
          <div class="flex items-center justify-center w-16 h-16 bg-gray-500 mr-4 rounded-full">
            <span class="font-medium text-3xl text-white uppercase">{{ initials }}</span>
          </div>
          <div class="flex-row self-center w-52">
            <div class="font-bold text-sm truncate">
              {{ userStore.profile.firstName }} {{ userStore.profile.lastName }}
            </div>
            <div class="text-xs truncate">{{ userStore.profile.email }}</div>
          </div>
        </div>
        <div class="border-t border-gray-800 text-gray-300 text-sm">
          <BaseButton
            :title="$t('glossary.myProfile')"
            class="group flex items-center px-3 py-2 hover:bg-gray-800 hover:text-white w-full"
            @click="onProfile()"
          >
            <SvgIcon
              :path="mdiCardAccountDetails"
              class="fill-gray-300 group-hover:fill-white h-5 w-5"
            />
            <div class="ms-2">{{ $t('glossary.myProfile') }}</div>
          </BaseButton>
          <BaseButton
            :title="$t('glossary.mySetting', 2)"
            class="group flex items-center px-3 py-2 hover:bg-gray-800 hover:text-white w-full"
            @click="onSettings()"
          >
            <SvgIcon :path="mdiAccountCog" class="fill-gray-300 group-hover:fill-white h-5 w-5" />
            <div class="ms-2">{{ $t('glossary.mySetting', 2) }}</div>
          </BaseButton>
          <a target="_blank" href="https://addimotion.com/support">
            <BaseButton
              :title="$t('glossary.customerSupport')"
              class="group flex items-center justify-between px-3 py-2 hover:bg-gray-800 hover:text-white w-full"
            >
              <div class="flex items-center">
                <SvgIcon
                  :path="mdiCommentQuestion"
                  class="fill-gray-300 group-hover:fill-white h-5 w-5"
                />
                <div class="ms-2">{{ $t('glossary.customerSupport') }}</div>
              </div>
              <SvgIcon :path="mdiOpenInNew" class="fill-gray-600 group-hover:fill-white h-5 w-5" />
            </BaseButton>
          </a>
          <BaseButton
            :title="$t('glossary.information')"
            class="group flex items-center px-3 py-2 hover:bg-gray-800 hover:text-white w-full"
            @click="onAbout()"
          >
            <SvgIcon
              :path="mdiInformationOutline"
              class="fill-gray-300 group-hover:fill-white h-5 w-5"
            />
            <div class="ms-2">{{ $t('glossary.information') }}</div>
          </BaseButton>
          <div class="border-t border-gray-800">
            <BaseButton
              :title="$t('glossary.signOut')"
              class="group flex items-center px-3 py-2 hover:bg-gray-800 hover:text-white w-full"
              @click="signOut"
            >
              <SvgIcon :path="mdiLogout" class="fill-gray-300 group-hover:fill-white h-5 w-5" />
              <div class="ms-2">{{ $t('glossary.signOut') }}</div>
            </BaseButton>
          </div>
        </div>
      </div>
    </ScaleTransition>
    <UserProfileModal v-model:open="isProfileOpen" />
    <InformationModal v-model:open="isAboutOpen" />
    <UserSettingsModal v-model:open="isSettingsOpen" />
  </div>
</template>
